import { withRouter } from 'react-router-dom';

import { ProductActions as AdvoxProductActions } from 'AdvoxComponent/ProductActions/ProductActions.component';
import CategoriesTag from 'Component/CategoriesTag';
import CmsBlock from 'Component/CmsBlock';
import EkomiPDPWidget from 'Component/EkomiPDPWidget';
import LowestPriceIn30Days from 'Component/LowestPriceIn30Days';
import PRODUCT_TYPE from 'Component/Product/Product.config';
import ProductAdditionalInfo from 'Component/ProductAdditionalInfo';
import ProductRelatedAccessories from 'Component/ProductRelatedAccessories';
import PromoBanner from 'Component/PromoBanner';
import RelatedProductsByAttribute from 'Component/RelatedProductsByAttribute';
import RewardsPointsForProduct from 'Component/RewardsPointsForProduct';
import SocialShare from 'Component/SocialShare';
import TextPlaceholder from 'Component/TextPlaceholder';
import TypographyHeader from 'Component/TypographyHeader';
import { GRID_LAYOUT } from 'Route/CategoryPage/CategoryPage.config';
import { REWARD_POINTS_PDP } from 'Route/ProductPage/ProductPage.config';
import { isCrawler, isSSR } from 'Util/Browser';

import './ProductActions.style';

/** @namespace Pwa/Component/ProductActions/Component */
export class ProductActions extends AdvoxProductActions {
    componentDidUpdate(prevProps) {
        const {
            product: { id: prevId },
        } = prevProps;
        const {
            product: { id },
            minQuantity,
            setQuantity,
        } = this.props;

        if (id !== prevId) {
            setQuantity(minQuantity);
        }
    }

    renderSchema() {
        const { productName, stockMeta, metaLink } = this.props;

        return (
            <>
                {this.renderOfferCount()}
                <meta itemProp="availability" content={stockMeta} />
                <meta itemProp="url" content={metaLink} />
                <a block="ProductActions" elem="SchemaUrl" href={metaLink}>
                    {productName}
                </a>
            </>
        );
    }

    renderAdditionalInfoBlock() {
        const {
            product: {
                price_range,
                shipping_costs,
                shipping_costs_additional_methods,
                calculated_delivery_time,
                calculated_delivery_cost,
            },
            showAlternativeDesign,
        } = this.props;

        if (!price_range) return;

        const {
            minimum_price: {
                final_price: { value },
            },
        } = price_range;

        return (
            <ProductAdditionalInfo
                productPrice={value}
                calculatedDeliveryTime={calculated_delivery_time}
                calculatedDeliveryCost={calculated_delivery_cost}
                shippingCosts={shipping_costs}
                shippingCostsAdditional={shipping_costs_additional_methods}
                showAlternativeDesign={showAlternativeDesign}
            />
        );
    }

    renderCategoriesTag() {
        const {
            product: { product_related_categories },
        } = this.props;

        if (!product_related_categories) return null;

        return <CategoriesTag items={product_related_categories.items} />;
    }

    renderSocialShareBlock() {
        return <SocialShare />;
    }

    renderProductRewardPoints() {
        const {
            product: { id },
        } = this.props;

        return id && <RewardsPointsForProduct productId={id} />;
    }

    renderProductRelatedAccessories() {
        const {
            product: { product_related_accessories: products, id },
        } = this.props;

        return id && <ProductRelatedAccessories productsRelated={products} />;
    }

    renderLowestPriceIn30Days() {
        const {
            product: { lowest_price },
        } = this.props;

        return (
            lowest_price && (
                <div block="ProductActions" elem="Omnibus">
                    <LowestPriceIn30Days lowestPrice={lowest_price} />
                </div>
            )
        );
    }

    renderShowRewardPoints() {
        return <CmsBlock identifier={REWARD_POINTS_PDP} />;
    }

    renderSKU() {
        const {
            product: { sku },
        } = this.props;

        if (!sku) return;
        return (
            <div block="ProductActions" elem="SKU">
                <p>{`SKU: ${sku}`}</p>
            </div>
        );
    }

    renderDesktop() {
        return (
            <>
                {this.renderName()}
                {this.renderReviewSection()}
                <div block="ProductActions" elem="Wrapper">
                    {this.renderPriceWithGlobalSchema()}
                    {this.renderLowestPriceIn30Days()}
                    {this.renderSKU()}
                    {this.renderProductRewardPoints()}
                    {this.renderRelatedProductsByAttribute()}
                    {this.renderConfigurableOptions()}
                    {this.renderCustomAndBundleOptions()}
                    {this.renderGroupedOptions()}
                    {this.renderDownloadableSamples()}
                    {this.renderDownloadableLinks()}
                    {this.renderTierPrices()}
                    {this.renderProductRelatedAccessories()}
                    {this.renderShowRewardPoints()}
                    {this.renderAddToCartActionBlock()}
                    {this.renderAdditionalInfoBlock()}
                    {this.renderCategoriesTag()}
                    {this.renderBanner('ProductMiddle', 'productMiddle')}
                    {this.renderBanner('ProductBottom', 'productBottom')}
                    {this.renderSocialShareBlock()}
                </div>
            </>
        );
    }

    renderMobile() {
        return (
            <>
                {this.renderTierPrices()}
                {this.renderPriceWithGlobalSchema()}
                {this.renderLowestPriceIn30Days()}
                {this.renderSKU()}
                <div block="ProductActions" elem="Wrapper">
                    {this.renderReviewSection()}
                    {this.renderProductRewardPoints()}
                    {this.renderRelatedProductsByAttribute()}
                    {this.renderConfigurableOptions()}
                    {this.renderCustomAndBundleOptions()}
                    {this.renderGroupedOptions()}
                    {this.renderDownloadableSamples()}
                    {this.renderDownloadableLinks()}
                    {this.renderProductRelatedAccessories()}
                    {this.renderShowRewardPoints()}
                    {this.renderAddToCartActionBlock()}
                    {this.renderAdditionalInfoBlock()}
                    {this.renderCategoriesTag()}
                    {this.renderBanner('ProductMiddle', 'productMiddle')}
                    {this.renderBanner('ProductBottom', 'productBottom')}
                    {this.renderSocialShareBlock()}
                    {this.renderAddToCartMobile()}
                </div>
            </>
        );
    }

    renderAddToCartMobile() {
        const { showFixedAddToCart } = this.props;

        return (
            <div
                block="ProductActions"
                elem="AddToCartFixed"
                mods={{ isPrerendered: isSSR() || isCrawler(), visible: showFixedAddToCart }}
            >
                {this.renderAddToCartButton(GRID_LAYOUT, true)}
            </div>
        );
    }

    renderAddToCartActionBlock() {
        return (
            <div
                id="ProductActionsAddToCartWrapper"
                block="ProductActions"
                elem="AddToCartWrapper"
                mods={{ isPrerendered: isSSR() || isCrawler() }}
            >
                {this.renderQuantityChanger()}
                {this.renderAddToCartButton()}
                <div block="ProductActions" elem="ActionButtons">
                    {this.renderBanner('ProductInfo', 'productInfo')}
                </div>
            </div>
        );
    }

    renderRelatedProductsByAttribute() {
        const {
            product: { related_products_by_attribute, related_products_by_attribute_label },
        } = this.props;

        return (
            <RelatedProductsByAttribute
                products={related_products_by_attribute}
                label={related_products_by_attribute_label}
            />
        );
    }

    renderName() {
        const {
            product: { name },
        } = this.props;
        const nameToRender = name;

        return (
            <TypographyHeader
                fontWeight="bold"
                variant="normal"
                mix={{ block: 'ProductActions', elem: 'Title' }}
                itemProp="name"
            >
                <TextPlaceholder content={nameToRender} length="medium" />
            </TypographyHeader>
        );
    }

    renderPriceWithGlobalSchema() {
        const { inStock, product: { type_id: baseType } = {} } = this.props;

        if (baseType === PRODUCT_TYPE.bundle && !inStock) {
            return null;
        }

        return super.renderPriceWithSchema();
    }

    renderBanner(elem, section) {
        const {
            product: { id },
        } = this.props;

        return id ? (
            <div block="ProductActions" elem={`${elem}Banner`}>
                <PromoBanner section={section} productId={id} />
            </div>
        ) : null;
    }

    renderReviewSection() {
        const {
            product: { id },
        } = this.props;

        return (
            <div block="ProductActions" elem="Reviews">
                <EkomiPDPWidget productId={id} type="renderStars" />
            </div>
        );
    }
}

export default withRouter(ProductActions);
